import { FC, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import Slider from 'modules/auth/components/Slider';
import MyriamIDPForm from 'modules/auth/components/MyriamIDPForm';
import { getLoginByCorporateId } from 'modules/auth/utils/getLoginByCorporateId';

import 'assets/styles/Pages.scss';
import 'assets/styles/Styles.scss';

import {
  clearPostAuthRedirect,
  peekSanitizedPostAuthRedirect,
} from 'utils/postAuthRedirect';
import { sanitizeInternalRedirectPath } from 'utils/sanitizeInternalRedirectPath';
import {
  hasInternalView,
  hasMyriamIdpLogin,
  hasShowDetails,
} from 'utils/showDetails';
import { useTranslate } from 'hooks/useTranslate';
import { useCustomAuth } from '../../contexts/CustomAuthContext';
import CustomLoginForm from '../../components/CustomLoginForm';
import { debugAuth } from '../../utils/debugAuth';

import LoginFooter from '../Login/LoginFooter';
import LoginHeader from '../Login/LoginHeader';
import LoginWarning from '../Login/LoginWarning';

type CustomLoginProps = {
  blockedMode?: boolean;
};

const CustomLogin: FC<CustomLoginProps> = ({ blockedMode }) => {
  const { t } = useTranslate();
  const history = useHistory();
  const location = useLocation();
  const { isAuthenticated, user } = useCustomAuth();

  // Debug authentication configuration
  useEffect(() => {
    if (hasInternalView()) {
      debugAuth.enable();
    }
  }, []);

  // SAML: If we landed on login with a code in URL (Cognito callback), redirect to /callback to process it
  useEffect(() => {
    if (!hasMyriamIdpLogin()) return;

    const searchParams = new URLSearchParams(window.location.search);
    let code = searchParams.get('code');
    if (!code && window.location.hash.includes('?')) {
      const hashQuery = window.location.hash.split('?')[1] || '';
      code = new URLSearchParams(hashQuery).get('code');
    }
    if (code) {
      const hash = `#/callback?code=${encodeURIComponent(code)}`;
      window.location.replace(`${window.location.origin}${window.location.pathname}${hash}`);
    }
  }, []);

  const { pageStyle, warning } = getLoginByCorporateId();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const pending = peekSanitizedPostAuthRedirect();
      if (pending) {
        clearPostAuthRedirect();
        history.push(pending);
        return;
      }
      const returnUrl = new URLSearchParams(location.search).get('returnUrl');
      if (returnUrl) {
        const safe =
          sanitizeInternalRedirectPath(returnUrl) ||
          sanitizeInternalRedirectPath(`/${returnUrl}`);
        history.push(safe || '/');
      } else {
        history.push('/');
      }
    }
  }, [isAuthenticated, user, history, location.search]);

  const handleLoginSuccess = () => {
    // The useEffect above will handle the redirect
  };

  const handleLoginError = (error: string) => {
    console.error('Login error:', error);
  };

  // Don't render the form if already authenticated
  if (isAuthenticated) {
    return null;
  }

  const isTemporaryLoginBlocked = false;

  return (
    <div className={`nk-split nk-split-page nk-split-md ${pageStyle}`}>
      <div className="nk-split-content nk-block-area nk-block-area-column nk-auth-container bg-white">
        <div className="nk-block nk-block-middle nk-auth-body">
          <LoginHeader blockedMode={isTemporaryLoginBlocked} />

          {isTemporaryLoginBlocked && (
            <div className="nk-block-head">
              <div className="nk-block-head-content">
                <h5 className="nk-block-title">
                  {t('MaintenanceWarningTitle')}
                </h5>
                <div className="nk-block-des">
                  <p>{t('MaintenanceWarningDescription')}</p>
                </div>
              </div>
            </div>
          )}

          {!isTemporaryLoginBlocked && (
            <CustomLoginForm
              onSuccess={handleLoginSuccess}
              onError={handleLoginError}
            />
          )}

          {!isTemporaryLoginBlocked && hasMyriamIdpLogin() && <MyriamIDPForm />}

          {!isTemporaryLoginBlocked && <LoginWarning warning={warning} />}
        </div>

        <LoginFooter />
      </div>
      <Slider />
    </div>
  );
};

export default CustomLogin;
